import CopyAllOutlined from '@mui/icons-material/CopyAllOutlined';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import EditIcon from '@mui/icons-material/Edit';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import React, { useMemo, useState } from 'react';

import { AnimateComponentPresence } from '~components/AnimatePresence';
import { CardItemLabelValue } from '~components/Common/CardItemLabelValue';
import { useCompanyCurrency } from '~hooks/useCompanyCurrency/useCompanyCurrency';
import {
  Rate,
  RateCommissionInputs,
  RateFlatCommissionInputs,
  RateFlatRateInputs,
  RatePerUnitInputs,
  RateTimeInputs,
} from '~hooks/useRates/models';
import {
  getFormattedRateValue,
  rateTypeLabelColor,
} from '~pages/Settings/RatesManagement/rateUtils';
import { useStores } from '~store';

interface RateCardProps {
  rate: Rate;
  onEdit: (id: string) => void;
  deleteClickCallBack: (id: string) => void;
  copyClickCallBack?: (id: string) => void;
}

const RateCard = observer(
  ({ rate, onEdit, deleteClickCallBack, copyClickCallBack }: RateCardProps) => {
    const theme = useTheme();
    const [isHovered, setIsHovered] = useState<boolean>(false);
    const { userStore } = useStores();
    const { currencyFormatter } = useCompanyCurrency();
    const userPermissions = useMemo(() => {
      return userStore.getPermissions();
    }, [userStore.getPermissions()]);
    const rateDataVariables = (rate: Rate) => {
      const projects = rate?.projects.map((p) => p.name).join(', ') || '';
      const equipment = rate.equipment.map((e) => e.name).join(', ') || '';
      const services = rate.services.map((s) => s.name).join(', ') || '';

      const result = [projects, equipment, services].filter(Boolean).join(', ');

      return result;
    };

    const ratePerUnitInputsView = (rateInputs: RatePerUnitInputs) => {
      return (
        <CardItemLabelValue
          isInline={true}
          label={t('administration.rates.minimum')}
          value={`${rateInputs?.perUnitMinimum}`}
        />
      );
    };

    const rateHourlyInputsView = (rateInputs: RateTimeInputs) => {
      return (
        <>
          <CardItemLabelValue
            isInline={true}
            label={t('administration.rates.minimum')}
            value={`${rateInputs?.timeMinimumMinutes} ${t(
              'administration.rates.minutes_abbreviation',
            )}`}
          />
          <Divider flexItem={true} sx={{ mx: 2 }} orientation={'vertical'}></Divider>
          <CardItemLabelValue
            isInline={true}
            label={t('administration.rates.increment')}
            value={`${rateInputs?.timeIncrementMinutes} ${t(
              'administration.rates.minutes_abbreviation',
            )}`}
          />
        </>
      );
    };

    const rateTimeInputsView = (rateInputs: RateTimeInputs) => (
      <>
        <CardItemLabelValue
          isInline={true}
          label={t('administration.rates.minimum')}
          value={rateInputs?.timeMinimumMinutes}
        />
      </>
    );

    const getRelevantRateInputsView = (rate: Rate) => {
      const shouldShowRateDetails = !(
        rate?.isCommission ||
        rate?.isFlatCommission ||
        rate?.isFlatRate
      );
      if (!shouldShowRateDetails) {
        return;
      }

      const inputs = rate.rateInputs;
      if (rate?.isHourly) {
        return rateHourlyInputsView(inputs as RateTimeInputs);
      } else if (rate?.isDaily) {
        return rateTimeInputsView(inputs as RateTimeInputs);
      } else {
        return ratePerUnitInputsView(inputs as RatePerUnitInputs);
      }
    };

    const getRelevantRateValueWithUnitLabel = (rate: Rate) => {
      const typeLabel = rateTypeLabelColor(rate?.type?.value)?.label;
      let rateValue = 0;

      if (rate?.rateInputs instanceof RateTimeInputs) {
        rateValue = rate.rateInputs.timeRate;
      } else if (rate?.rateInputs instanceof RateCommissionInputs) {
        rateValue = rate.rateInputs.commissionPercentage;
      } else if (rate?.rateInputs instanceof RateFlatCommissionInputs) {
        rateValue = rate.rateInputs.commissionAmount;
      } else if (rate?.rateInputs instanceof RateFlatRateInputs) {
        rateValue = rate.rateInputs.flatAmount;
      } else if (rate?.rateInputs instanceof RatePerUnitInputs) {
        rateValue = rate.rateInputs.perUnitRate;
      }

      const value = getFormattedRateValue(rate.type.value, rateValue, currencyFormatter);

      return (
        <Typography variant="h6" color="grey">
          {`${value} ${typeLabel}`}
        </Typography>
      );
    };

    return (
      <Card
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        elevation={1}
        sx={{
          minWidth: 540,
          maxWidth: 540,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'start',
                flexDirection: 'column',
                width: '100%',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  justifyContent: 'space-between',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                  }}
                >
                  <Tooltip title={`${rate.name}`}>
                    <Typography
                      sx={{
                        maxWidth: '240px',
                        minWidth: '180px',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                        mr: 2,
                      }}
                      variant="h5"
                    >
                      {rate.name}
                    </Typography>
                  </Tooltip>
                  <Box
                    sx={{
                      display: 'flex',
                    }}
                  >
                    {rate.isPrimary && (
                      <Chip
                        sx={{ ml: 1 }}
                        size={'small'}
                        label={t('administration.rates.primary_rate') || ''}
                        color={'info'}
                        variant={'outlined'}
                      />
                    )}
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    justifySelf: 'end',
                  }}
                >
                  <AnimateComponentPresence
                    identifier={rate.id}
                    isVisible={isHovered}
                    animationDuration={0.3}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {userPermissions?.canEditRate && (
                        <>
                          {copyClickCallBack && (
                            <IconButton
                              aria-label="copy-rate"
                              onClick={() => {
                                copyClickCallBack(rate?.id as string);
                              }}
                            >
                              <CopyAllOutlined sx={{ height: 18, width: 18 }} />
                            </IconButton>
                          )}

                          <IconButton
                            aria-label="edit-rate"
                            onClick={() => {
                              onEdit(rate?.id as string);
                            }}
                          >
                            <EditIcon sx={{ height: 22, width: 22 }} />
                          </IconButton>
                        </>
                      )}

                      {userPermissions?.canDeleteRate && (
                        <IconButton
                          aria-label="delete-rate"
                          onClick={() => {
                            deleteClickCallBack(rate?.id as string);
                          }}
                        >
                          <DeleteOutlined sx={{ height: 22, width: 22 }} />
                        </IconButton>
                      )}
                    </Box>
                  </AnimateComponentPresence>
                </Box>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  mt: 2,
                }}
              >
                {getRelevantRateValueWithUnitLabel(rate)}
              </Box>
            </Box>
          </Box>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {rateDataVariables(rate)}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              width: '100%',
              alignItems: 'center',
              justifyContent: 'flex-start',
            }}
          >
            {getRelevantRateInputsView(rate)}
          </Box>
        </CardContent>
      </Card>
    );
  },
);
export { RateCard };
